/**
 * Tema dos gráficos (recharts).
 *
 * Recharts escreve as cores como ATRIBUTO de SVG (fill="…", stroke="…") e
 * atributo de apresentação não resolve var(--token) — por isso aqui a cor não
 * pode ser a string 'var(--x)': ela tem de ser o valor JÁ RESOLVIDO.
 * A resolução é feita em tempo de render, lendo o token do <html> com o tema
 * corrente. Assim o gráfico acompanha claro/escuro/black em vez de ficar
 * congelado no escuro, e nenhum hex de tema vive fora de tokens.css.
 */
import { useMemo } from 'react';

import { useTheme } from '@/store/useTheme';

/** Lê um token do tema corrente. O 2º argumento só vale onde não há CSS
 *  aplicado (jsdom nos testes, primeiro paint antes do CSS). */
function token(nome: string, semCss: string): string {
  if (typeof document === 'undefined') return semCss;
  const v = getComputedStyle(document.documentElement).getPropertyValue(nome).trim();
  return v || semCss;
}

export interface ChartTheme {
  background: string;
  grid: string;
  axis: string;
  text: string;
  textMuted: string;
  brand: string;
  brandGlow: string;
  brandBg: string;
  ok: string;
  warn: string;
  crit: string;
  series: string[];
}

export interface ChartTooltipStyle {
  backgroundColor: string;
  border: string;
  borderRadius: number;
  padding: number;
  fontSize: number;
  fontFamily: string;
  color: string;
}

function resolve(): { chartTheme: ChartTheme; chartTooltipStyle: ChartTooltipStyle } {
  const chartTheme: ChartTheme = {
    background: token('--bg-1', '#11141b'),
    grid: token('--border-2', '#2a2f3d'),
    axis: token('--text-4', '#6e7587'),
    text: token('--text-2', '#c5cad4'),
    textMuted: token('--text-3', '#9aa1b3'),

    brand: token('--nexus-brand', '#3b82f6'),
    brandGlow: token('--nexus-brand-glow', 'rgba(59, 130, 246, 0.35)'),
    brandBg: token('--nexus-brand-bg', 'rgba(59, 130, 246, 0.14)'),

    ok: token('--sig-ok', '#10b981'),
    warn: token('--sig-warn', '#f59e0b'),
    crit: token('--sig-crit', '#ef4444'),

    // paleta categórica: identifica a SÉRIE (editoria, provedor), por isso os
    // quatro primeiros reaproveitam os sinais e os demais têm token próprio
    series: [
      token('--nexus-brand', '#3b82f6'),
      token('--sig-ok', '#10b981'),
      token('--sig-warn', '#f59e0b'),
      token('--sig-crit', '#ef4444'),
      token('--chart-serie-5', '#8b5cf6'),
      token('--chart-serie-6', '#ec4899'),
      token('--chart-serie-7', '#14b8a6'),
      token('--chart-serie-8', '#84cc16'),
    ],
  };

  const chartTooltipStyle: ChartTooltipStyle = {
    backgroundColor: token('--bg-2', '#181c25'),
    border: `1px solid ${token('--border-2', '#2a2f3d')}`,
    borderRadius: 4,
    padding: 8,
    fontSize: 12,
    fontFamily: 'JetBrains Mono, monospace',
    color: token('--text-1', '#f5f6f8'),
  };

  return { chartTheme, chartTooltipStyle };
}

/** Cores do gráfico no tema corrente. Recalcula quando o tema muda. */
export function useChartTheme(): { chartTheme: ChartTheme; chartTooltipStyle: ChartTooltipStyle } {
  const tema = useTheme((s) => s.theme);
  return useMemo(() => resolve(), [tema]);
}
